import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { refundPaymentSchema, type RefundPaymentInput } from '../schemas';
import { useRefundPayment } from '../hooks';
import type { AdminPaymentRow } from '../types';

interface Props {
  open: boolean;
  payment: AdminPaymentRow;
  onClose: () => void;
}

function mintIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * One idempotency key per refund attempt, kept at module level (the modal unmounts when closed, so a
 * per-mount ref gave every re-open a brand-new key). Re-submitting the SAME refund — e.g. after a timeout or
 * an "unclear outcome" error — reuses the key, so the backend replays/re-checks the earlier attempt instead of
 * sending a second refund. Cleared only on success; a changed amount is a different request, so it gets a new key.
 */
const attemptKeys = new Map<string, { key: string; amount: number }>();

function keyFor(paymentId: string, amount: number): string {
  const existing = attemptKeys.get(paymentId);
  if (existing && existing.amount === amount) return existing.key;
  const entry = { key: mintIdempotencyKey(), amount };
  attemptKeys.set(paymentId, entry);
  return entry.key;
}

// Backend `amount` is paise (RefundPaymentDto -> invoice.totalPaise); the form
// edits major units (rupees) and converts on submit.
export function RefundPaymentModal({ open, payment, onClose }: Props) {
  const mutation = useRefundPayment(payment.userId);
  const maxMajor = payment.amount / 100;
  // FI-01 / H3: a wallet pack refunds "paid − credits used" — pre-filled, editable (the backend audits a change).
  const pack = payment.pack ?? null;
  const defaultMajor = pack ? pack.suggestedRefundPaise / 100 : maxMajor;
  const defaultCategory = pack ? ('PACK_REFUND' as const) : ('SERVICE_FAULT' as const);
  const form = useForm<RefundPaymentInput>({
    resolver: zodResolver(refundPaymentSchema),
    defaultValues: { reason: '', category: defaultCategory, amount: defaultMajor, maxAmount: payment.amount },
  });

  useEffect(() => {
    form.reset({
      reason: '',
      category: defaultCategory,
      overrideWindowReason: '',
      amount: defaultMajor,
      maxAmount: payment.amount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.id, payment.amount, pack?.suggestedRefundPaise, open]);

  const submit = form.handleSubmit(async (values) => {
    // D-19 (2026-10-13): no partial refunds — the amount is never sent; the backend refunds what remains.
    // The one exception is a wallet pack (FI-01 / H3): the admin's amount (pre-filled "paid − used") is sent.
    const amountPaise = pack && values.amount !== undefined ? Math.round(values.amount * 100) : undefined;
    try {
      await mutation.mutateAsync({
        paymentId: payment.id,
        amount: amountPaise,
        reason: values.reason,
        category: values.category,
        overrideWindowReason: values.overrideWindowReason?.trim() || undefined,
        // Only an amount the admin CHANGED is an override; an unchanged pre-fill that has gone stale (credits used since
        // the page loaded) is refused by the backend with the fresh amount (409 PACK_USAGE_CHANGED).
        overrideSuggestion: !!pack && amountPaise !== undefined && amountPaise !== pack.suggestedRefundPaise,
        idempotencyKey: keyFor(payment.id, amountPaise ?? 0),
      });
    } catch {
      return; // surfaced via mutation.error below; keep the key so a retry replays
    }
    attemptKeys.delete(payment.id);
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title="Refund payment"
      description={`Original amount: ${payment.currency} ${maxMajor.toFixed(2)}. The refund is submitted to the gateway immediately but confirmed asynchronously — it will show as "Refund Pending" until confirmed.`}
      confirmLabel="Refund"
      destructive
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit()}
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Refund type
          </label>
          <select
            {...form.register('category')}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
          >
            <option value="SERVICE_FAULT">Service fault — template / image / publish problem (24 h; verify the invitation first)</option>
            <option value="PAYMENT_ISSUE">Payment issue — duplicate / unauthorized / failed (verify in Razorpay; no 24 h limit)</option>
            <option value="PACK_REFUND">Pack refund — unused credits (paid − credits used)</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Amount ({payment.currency}) — {pack ? 'pack: paid − credits used' : 'full refund only'}
          </label>
          {pack && (
            <p className="text-[11px] text-on-surface-variant mb-1" data-testid="pack-usage">
              {pack.creditsUsed.toLocaleString('en-IN')} of {pack.creditsGranted.toLocaleString('en-IN')} credits used. Suggested refund:{' '}
              {payment.currency} {(pack.suggestedRefundPaise / 100).toFixed(2)}. You can change it; a change is recorded.
            </p>
          )}
          <input
            type="number"
            step="0.01"
            readOnly={!pack}
            title={pack ? 'Pre-filled with paid − credits used; editable' : 'Refunds are always for the full remaining amount (no partial refunds)'}
            {...form.register('amount', {
              setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
            })}
            className={`w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm ${pack ? 'focus:outline-none focus:ring-2 focus:ring-red-500/30' : 'opacity-70 cursor-not-allowed'}`}
          />
          {form.formState.errors.amount && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.amount.message}</p>
          )}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Reason
          </label>
          <textarea
            {...form.register('reason')}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
            placeholder="Why is this payment being refunded? (min 10 chars)"
          />
          {form.formState.errors.reason && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.reason.message}</p>
          )}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Override reason (service fault without a fresh flag)
          </label>
          <input
            {...form.register('overrideWindowReason')}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
            placeholder="Service fault only: why you are refunding without a fresh flagged fault"
          />
          <p className="text-[11px] text-on-surface-variant mt-1">
            A service-fault refund needs a flagged fault (system-detected or &quot;Confirm fault&quot;) no older than 24 hours. Without one, enter a reason (audited). Payment issues (duplicate / unauthorized / failed) need no override.
          </p>
        </div>
        {mutation.error && (
          <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>
        )}
      </form>
    </ConfirmModal>
  );
}
